import React, { useState, useCallback, useEffect } from "react";
import { useUser } from "../../contexts/UserContext";
import { TransactionFilters } from "./components/TransactionFilters";
import { TransactionTable } from "./components/TransactionTable";
import { BulkActions } from "./components/BulkActions";
import { useTransactionData } from "./hooks/useTransactionData";
import LoadingState from "../common/LoadingState";
import ErrorState from "../common/ErrorState";
import type { TransactionFilters as TFilters } from ".";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../PageHeader";
import { FiDownload } from "react-icons/fi";
import { useFamilyBankAccounts } from "../../domain/services/hooks/useFamilyBankAccounts";
import { BankAccount } from "../../domain/types/banks/BankAccount";
import { Bank } from "../../domain/types/banks/Bank";
import { FirestoreBankRepository } from "../../infrastructure/repositories/BankRepository";
import { Transaction } from "../../domain/types/Transaction";
import { exportTransactionsToCSV } from "./utils/exportUtils";

const TransactionsList: React.FC = () => {
  const { currentUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { familyBankAccounts, loading: accountsLoading } =
    useFamilyBankAccounts();

  const [accountsById, setAccountsById] = useState<{
    [accountId: string]: BankAccount;
  }>({});
  const [banksById, setBanksById] = useState<{ [bankId: string]: Bank }>({});

  useEffect(() => {
    if (!accountsLoading && familyBankAccounts) {
      const accountsMap: { [accountId: string]: BankAccount } = {};
      const bankIds = new Set<string>();

      Object.values(familyBankAccounts).forEach((accounts) => {
        accounts.forEach((account) => {
          accountsMap[account.accountId] = account;
          if (account.bankId) {
            bankIds.add(account.bankId);
          }
        });
      });

      setAccountsById(accountsMap);

      // Fetch Banks
      const fetchBanks = async () => {
        try {
          const bankRepository = new FirestoreBankRepository();
          const bankPromises = Array.from(bankIds).map((bankId) =>
            bankRepository.getBankById(bankId),
          );
          const bankResults = await Promise.all(bankPromises);
          const banksMap: { [bankId: string]: Bank } = {};
          bankResults.forEach((bank, index) => {
            if (bank) {
              banksMap[Array.from(bankIds)[index]] = bank;
            }
          });
          setBanksById(banksMap);
        } catch (error) {
          console.error("Error fetching banks:", error);
        }
      };

      fetchBanks();
    }
  }, [accountsLoading, familyBankAccounts]);

  const initialFilters: TFilters = {
    type: searchParams.get("type") || "all",
    member: searchParams.get("member") || "all",
    account: searchParams.get("account") || "all",
    groupBy: (searchParams.get("groupBy") as TFilters["groupBy"]) || "none",
    searchTerm: searchParams.get("search") || "",
    dateRange: {
      start: searchParams.get("startDate")
        ? new Date(searchParams.get("startDate") || "")
        : null,
      end: searchParams.get("endDate")
        ? new Date(searchParams.get("endDate") || "")
        : null,
    },
  };

  const [filters, setFilters] = useState<TFilters>(initialFilters);
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transaction[]
  >([]);

  const handleFilterChange = useCallback(
    (newFilters: TFilters) => {
      setFilters(newFilters);
      setSelectedTransactions([]); // Clear selection when filters change

      // Update URL params
      const params = new URLSearchParams();
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          if (key === "dateRange") {
            if (value.start) params.set("startDate", value.start.toISOString());
            if (value.end) params.set("endDate", value.end.toISOString());
          } else {
            params.set(key, value.toString());
          }
        }
      });
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const { transactions, isLoading, error } = useTransactionData(
    currentUser,
    filters,
  );

  const handleSelectionChange = useCallback(
    (selectedTransactions: Transaction[]) => {
      setSelectedTransactions(selectedTransactions);
    },
    [],
  );

  const handleExport = useCallback(async () => {
    await exportTransactionsToCSV(transactions);
  }, [transactions]);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm">
      <PageHeader
        title="Transactions"
        description="View and manage your transaction history"
        actions={
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium rounded-md"
          >
            <FiDownload className="mr-2" />
            Export
          </button>
        }
        backgroundImage="/assets/transactions-bg.jpg"
        showBackButton={false}
      />

      <TransactionFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {selectedTransactions.length > 0 && (
        <BulkActions
          selectedCount={selectedTransactions.length}
          onClearSelection={() => setSelectedTransactions([])}
          selectedTransactions={selectedTransactions}
        />
      )}

      <div className="relative">
        {(isLoading || accountsLoading) && (
          <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 flex items-center justify-center">
            <LoadingState message="Fetching transactions..." />
          </div>
        )}
        {error ? (
          <ErrorState error={error} />
        ) : (
          <TransactionTable
            transactions={transactions}
            groupedBy={filters.groupBy}
            onSelectionChange={handleSelectionChange}
            accountsById={accountsById}
            banksById={banksById}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionsList;
